export const CHART_PALETTE = {
  // `brand` — Animas brand blue as a named chart color. Use for "this is
  // our primary KPI" emphasis (active line in multi-series comparison,
  // highlighted bar in a ranking). Resolves via `var(--brand-primary)` with
  // the Animas default (#0e76bc) as the fallback. The CSS var indirection
  // is inert today (applyBrand is dormant — see dashboard-shell/src/brand/
  // applyBrand.ts) but preserved for future re-enable.
  brand: "var(--brand-primary, #0e76bc)",
  primary: "#3b82f6",
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
  secondary: "#8b5cf6",
  neutral: "#6b7280",
  accent: "#ec4899",
  /** Informational cyan — semantically distinct from brand. Stays cyan regardless of tenant. */
  info: "#06b6d4",
  // Finance semantics (mapped to existing palette values)
  revenue: "#3b82f6",
  expenses: "#ef4444",
  net: "#10b981",
  profit: "#10b981",
  margin: "#f59e0b",
  cash: "#06b6d4",
  mrr: "#8b5cf6",
  // A/R aging buckets
  agingCurrent: "#10b981",
  aging1to30: "#3b82f6",
  aging31to60: "#f59e0b",
  aging61to90: "#f97316",
  agingOver90: "#ef4444",
} as const;

export const CHART_SERIES_COLORS = [
  CHART_PALETTE.primary,
  CHART_PALETTE.success,
  CHART_PALETTE.warning,
  CHART_PALETTE.error,
  CHART_PALETTE.secondary,
  CHART_PALETTE.accent,
] as const;

export const CHART_PALETTE_DARK = {
  // Dark-mode brand highlight — resolves via `var(--brand-primary)` which
  // brand.css remaps to a lighter stop in html.dark for legibility.
  brand: "var(--brand-primary, #4bacf4)",
  primary: "#60a5fa",
  success: "#34d399",
  warning: "#fbbf24",
  error: "#f87171",
  secondary: "#a78bfa",
  neutral: "#9ca3af",
  accent: "#f472b6",
  info: "#22d3ee",
  // Finance semantics (brighter for dark mode)
  revenue: "#60a5fa",
  expenses: "#f87171",
  net: "#34d399",
  profit: "#34d399",
  margin: "#fbbf24",
  cash: "#22d3ee",
  mrr: "#a78bfa",
  // A/R aging buckets (brighter)
  agingCurrent: "#34d399",
  aging1to30: "#60a5fa",
  aging31to60: "#fbbf24",
  aging61to90: "#fb923c",
  agingOver90: "#f87171",
} as const;

export const CHART_SERIES_COLORS_DARK = [
  CHART_PALETTE_DARK.primary,
  CHART_PALETTE_DARK.success,
  CHART_PALETTE_DARK.warning,
  CHART_PALETTE_DARK.error,
  CHART_PALETTE_DARK.secondary,
  CHART_PALETTE_DARK.accent,
] as const;

/** Chart chrome colors for axes, grids, and tooltips */
export const CHART_CHROME = {
  grid: "#e2e8f0",
  axisText: "#64748b",
  tooltipBg: "#ffffff",
  tooltipBorder: "#e2e8f0",
  dotStroke: "#ffffff",
  // Foreground text/glyph color rendered on top of a saturated STATUS_COLORS
  // fill (e.g. heatmap status cells). White maximizes contrast against all
  // four status hues in both light and dark mode.
  onStatusFill: "#ffffff",
} as const;

export const CHART_CHROME_DARK = {
  grid: "#374151",
  axisText: "#9ca3af",
  tooltipBg: "#1f2937",
  tooltipBorder: "#374151",
  dotStroke: "#1f2937",
  onStatusFill: "#ffffff",
} as const;

/** Shared chart layout constants — eliminates hardcoded fontSize/spacing in Recharts props */
export const CHART_LAYOUT = {
  tickFontSize: 12,
  tickFontSizeSmall: 11,
  legendFontSize: 12,
  legendPaddingTop: 8,
  legendIconSize: 8,
  legendIconType: "circle" as const,
} as const;

export const STATUS_COLORS = {
  good: CHART_PALETTE.success,
  warning: CHART_PALETTE.warning,
  critical: CHART_PALETTE.error,
  neutral: CHART_PALETTE.neutral,
} as const;
